/**
 * How an amount is written on the table.
 *
 * ====================== ONE THING THIS MUST NEVER DO ============================
 * It must never print a currency symbol on tournament chips.
 *
 * That is not a style rule. Prize tournaments here are free to enter for every tier,
 * always — free entry is what removes CONSIDERATION, one of the three elements
 * (prize, chance, consideration) that together make a private lottery. A product that
 * writes "$12,400" on a tournament stack is telling the player their chips are money.
 * They are not: they are not redeemable, not transferable, and not a balance. The
 * cash game is the only place a currency symbol is a true statement, because there
 * the numbers really are the stakes the player chose.
 *
 * `formatMoney` therefore takes the FORMAT, not just the number, and there is no way
 * to ask it for a dollar sign on a tournament stack.
 * ================================================================================
 *
 * The toggle itself is chips ⇄ big blinds. Big blinds are how anyone actually
 * discusses a stack — "he's got twelve behind" — and they are the only unit in which
 * two different stake levels can be compared at all, which is the same reason the
 * leak report sums in them.
 */

/**
 * How the viewer wants amounts written. Three, since Phase 4.
 *
 *   bucks   the coin and a grouped number, wherever the amount really is Bucks —
 *           a balance, a lobby's stakes, a cash table's chips. The default, because
 *           the wallet is what funds every room and the coin is what says so.
 *   native  the same numbers with no mark: the felt read as a chip count.
 *   bb      big blinds, the only unit two stake levels can be compared in.
 *
 * Tournament chips are never Bucks and are never marked, in any mode: the buy-in was
 * Bucks, the stack is chips, and the difference is the whole of why free entry keeps
 * a prize tournament legal.
 */
export type MoneyMode = "bucks" | "native" | "bb";
/**
 * What kind of numbers this table deals in.
 *
 * `cash` is the only one that may carry a currency symbol, because there the numbers
 * really are the stakes the player chose. `tournament` is chips, which are not money.
 * `bucks` is Buster Bucks — free play money that funds every room — and it is written
 * with the brass coin from `lib/mark.tsx` and the word "Bucks", never with a `$`. The
 * reason is the same reason tournaments have no symbol, and it is written out in full
 * at the top of this file.
 */
export type TableFormat = "cash" | "tournament" | "bucks";

export const MONEY_MODES: readonly { id: MoneyMode; label: string; hint: string }[] = [
  { id: "bucks", label: "Bucks", hint: "Balances and stakes, marked with the coin" },
  { id: "native", label: "Chips", hint: "The numbers as the table deals them" },
  { id: "bb", label: "Big blinds", hint: "How stacks are actually talked about" },
];

/** Chips, formatted the way a table shows them: no decimals unless there are any. */
export function chips(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const r = Math.round(v * 100) / 100;
  return Math.abs(r % 1) < 1e-9 ? r.toFixed(0) : r.toFixed(2);
}

/**
 * Chips with thousands separators, for tournament stacks.
 *
 * A tournament stack is five or six digits by the late levels, and "124500" is not a
 * number anybody reads at a glance while a clock is running.
 */
export function chipsGrouped(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const r = Math.round(v * 100) / 100;
  return Math.abs(r % 1) < 1e-9
    ? r.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : r.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export interface MoneyContext {
  mode: MoneyMode;
  bigBlind: number;
  format: TableFormat;
}

/**
 * One amount, written for this table.
 *
 * Big blinds get one decimal below ten and none above: the difference between 8.5bb
 * and 9bb changes whether you are shoving, and the difference between 62bb and 62.4bb
 * changes nothing and costs a digit of legibility at every seat.
 */
export function formatMoney(v: number, ctx: MoneyContext): string {
  if (!Number.isFinite(v)) return "—";

  if (ctx.mode === "bb") {
    // A zero or negative big blind is a misconfigured table, not a reason to divide
    // by it. Falling back to chips keeps a broken config legible instead of printing
    // Infinity across every seat.
    if (!(ctx.bigBlind > 0)) return chips(v);
    const bb = v / ctx.bigBlind;
    const n = Math.abs(bb) < 10 ? Math.round(bb * 10) / 10 : Math.round(bb);
    return `${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1)}bb`;
  }

  /*
   * A cash table in Bucks mode prints no symbol either, and after Phase 4 that is the
   * only kind of cash table there is: every seat in the product is bought with Buster
   * Bucks, so `$` on one would be a claim about money that is not true. The branch
   * below survives for a table whose numbers really are somebody's own currency, which
   * this product does not have and — see the header — must not grow by accident.
   */
  if (ctx.format === "cash" && ctx.mode === "bucks") return chipsGrouped(v);

  // No currency symbol on tournament chips, and none on Bucks. See the header.
  // Bucks and tournament chips are written the same way — grouped, bare — because the
  // difference between them is what they ARE, not how a number looks; the coin is what
  // marks a balance as Bucks, and `bucksMarked` below is what tells a component to
  // draw it.
  return ctx.format === "cash" ? `$${chips(v)}` : chipsGrouped(v);
}

/**
 * The same amount for a screen reader, spelled out.
 *
 * "$12" and "12bb" are read aloud as "twelve" and "twelve bee bee" by most screen
 * readers, which is either wrong or gibberish. The table announces the pot on every
 * street, so this is not a decoration.
 */
export function speakMoney(v: number, ctx: MoneyContext): string {
  if (!Number.isFinite(v)) return "unknown";
  if (ctx.mode === "bb" && ctx.bigBlind > 0) {
    const bb = v / ctx.bigBlind;
    const n = Math.abs(bb) < 10 ? Math.round(bb * 10) / 10 : Math.round(bb);
    return `${n} big blind${n === 1 ? "" : "s"}`;
  }
  const n = chips(v);
  if (ctx.format === "cash" && ctx.mode !== "bucks") return `${n} dollars`;
  if (ctx.format === "cash") return `${chipsGrouped(v)} Bucks`;
  // "Bucks", not "chips": a screen reader is the one place the word has to do the work
  // the coin does everywhere else.
  return `${chipsGrouped(v)} ${ctx.format === "bucks" ? "Bucks" : "chips"}`;
}

/**
 * Whether an amount should be drawn with the Bucks coin in front of it.
 *
 * The coin is SVG and this module is deliberately plain TypeScript — it is imported by
 * node tests that have no React and no DOM, and keeping it that way is what lets the
 * currency-symbol rule be tested at all. So the string half lives here and the mark
 * half lives in `MoneyContext`, which asks this.
 *
 * Big blinds are exempt: "12bb" is a measure of the table, not a balance, and a coin in
 * front of it would be claiming the wrong thing.
 */
/**
 * PHASE 4: the mode decides too, not only the format.
 *
 * When there were two modes, "native" meant "however this table writes its numbers"
 * and a Bucks table marked its amounts in it. There are three now, and "Chips" is a
 * viewer saying *read the felt as a chip count* — so the coin comes off. It goes back
 * on in "Bucks", which is the default. Big blinds are exempt in every case: "12bb" is
 * a measure of the table, not a balance, and a coin in front of it claims the wrong
 * thing.
 *
 * The gauge, the lobby cards and the wallet page draw the coin themselves rather than
 * asking this: they are outside any table's provider, and a balance is a balance
 * whatever unit somebody prefers to read a felt in.
 */
export function bucksMarked(ctx: MoneyContext): boolean {
  return ctx.format === "bucks" && ctx.mode === "bucks";
}

/* ------------------------------------------------------------ persistence --- */

const KEY = "bb.money-mode";

/**
 * The viewer's choice, remembered on their own device.
 *
 * Wrapped in try/catch on both sides: storage throws outright in a private window on
 * some browsers and in any embedded context with site data blocked, and a table that
 * refuses to render because it could not remember a display preference is a far worse
 * bug than one that forgets.
 */
/**
 * Bucks is the default, at cash tables and in every lobby.
 *
 * A player who has chosen nothing should see the currency the product actually runs
 * on. Chips and big blinds are for somebody who has decided how they want to read a
 * felt, and both are one tap away in the Settings sheet.
 */
export function loadMoneyMode(): MoneyMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === "bb" || v === "native" || v === "bucks" ? v : "bucks";
  } catch {
    return "bucks";
  }
}

export function saveMoneyMode(m: MoneyMode): void {
  try { localStorage.setItem(KEY, m); } catch { /* not worth a broken table */ }
  for (const l of moneyListeners) l();
}

/**
 * Phase 2b: told when the mode changes, wherever it was changed.
 *
 * There are now TWO controls for one setting — the units toggle in the room header and
 * the same choice in the Settings sheet — and the sheet is opened from outside any
 * `MoneyProvider`, so it cannot write through the context. Without a notification the
 * two would disagree until the next mount, which is exactly the "second copy of a
 * setting" this module exists to prevent. `MoneyProvider` subscribes; nothing else
 * needs to.
 *
 * A plain Set and no DOM, so `tests/money.test.ts` can keep importing this under Node.
 */
const moneyListeners = new Set<() => void>();

export function onMoneyModeChange(l: () => void): () => void {
  moneyListeners.add(l);
  return () => { moneyListeners.delete(l); };
}
